import ActionsPopover from "./ActionsPopover";

import { CURRENCY_SYMBOLS } from "../../constants/currencies.constants";

import { formatUnitForDisplay } from "../../utils/Utils";
import {
  formatCipSequenceValue,
  sourceFormatter,
  typeFormatter,
} from "../../utils/tableDataFormatter";

const getSectionFormattedValue = (params, numeric = false) => {
  const value = params?.value;
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (!numeric) {
    return value;
  }

  const numericValue = parseFloat(value);
  if (Number.isNaN(numericValue)) {
    return "-";
  }

  return numericValue.toFixed(2);
};

export const CIP_TABLE_CONFIG = (
  t,
  temperature,
  mass,
  currency,
  fetchCIPs,
  showLoader,
  source,
  enableActions = false,
) => {
  const currencyUnit = CURRENCY_SYMBOLS[currency] || currency;
  const pricePerMassUnit =
    currencyUnit && mass
      ? `${currencyUnit}/${mass}`
      : currencyUnit || mass || "";

  // display chemical columns only for recurrent, bulk & user CIP tables
  // as we don't need to show chemical columns for cip identification table((not recurrent, bulk & user CIP tables)
  // we can hide them based on source prop
  const columns = [
    {
      id: 1,
      headerName: t("CIPTableStrings.srNo"),
      field: "srNo",
      flex: 0.5,
      minWidth: 70,
    },
    {
      id: 2,
      headerName: t("HistoricCipTableStrings.start_date"),
      field: "start_date",
      flex: 1,
      minWidth: 160,
    },
    {
      id: 3,
      headerName: t("HistoricCipTableStrings.end_date"),
      field: "end_date",
      flex: 1,
      minWidth: 160,
    },
    {
      id: 4,
      headerName: t("HistoricCipTableStrings.type"),
      field: "type",
      flex: 0.7,
      minWidth: 100,
      valueFormatter: (params) => typeFormatter(params, t),
    },
    {
      id: 5,
      headerName: t("HistoricCipTableStrings.cipSequence"),
      field: "cip_sequence",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => formatCipSequenceValue(params?.value),
    },
    {
      id: 6,
      headerName: t("HistoricCipTableStrings.alkaliChemical"),
      field: "alkali_chemical_name",
      flex: 1,
      minWidth: 170,
      valueFormatter: (params) => getSectionFormattedValue(params),
      hide: !source,
    },
    {
      id: 7,
      headerName: t("HistoricCipTableStrings.alkaliPrice", {
        unit: pricePerMassUnit,
      }),
      field: "alkali_price",
      flex: 1,
      minWidth: 130,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 8,
      headerName: t("HistoricCipTableStrings.alkaliUsed", { unit: mass }),
      field: "alkali_used_per_cip",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 9,
      headerName: t("HistoricCipTableStrings.alkaliPh"),
      field: "alkali_cip_ph",
      flex: 1,
      minWidth: 100,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 10,
      headerName: t("HistoricCipTableStrings.alkaliTemperature", {
        unit: formatUnitForDisplay(temperature),
      }),
      field: "alkali_cip_temp",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 11,
      headerName: t("HistoricCipTableStrings.alkaliDuration"),
      field: "alkali_cip_duration",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 12,
      headerName: t("HistoricCipTableStrings.additiveAlkaliChemical"),
      field: "additive_alkali_chemical_name",
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => getSectionFormattedValue(params),
      hide: !source,
    },
    {
      id: 13,
      headerName: t("HistoricCipTableStrings.additiveAlkaliPrice", {
        unit: pricePerMassUnit,
      }),
      field: "additive_alkali_price",
      flex: 1,
      minWidth: 140,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 14,
      headerName: t("HistoricCipTableStrings.additiveAlkaliUsed", {
        unit: mass,
      }),
      field: "additive_alkali_used_per_cip",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 15,
      headerName: t("HistoricCipTableStrings.additiveAlkaliPh"),
      field: "additive_alkali_ph",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 16,
      headerName: t("HistoricCipTableStrings.additiveAlkaliTemperature", {
        unit: formatUnitForDisplay(temperature),
      }),
      field: "additive_alkali_temp",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 17,
      headerName: t("HistoricCipTableStrings.additiveAlkaliDuration"),
      field: "additive_alkali_duration",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 18,
      headerName: t("HistoricCipTableStrings.acidChemical"),
      field: "acid_chemical_name",
      flex: 1,
      minWidth: 160,
      valueFormatter: (params) => getSectionFormattedValue(params),
      hide: !source,
    },
    {
      id: 19,
      headerName: t("HistoricCipTableStrings.acidPrice", {
        unit: pricePerMassUnit,
      }),
      field: "acid_price",
      flex: 1,
      minWidth: 130,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 20,
      headerName: t("HistoricCipTableStrings.acidUsed", { unit: mass }),
      field: "acid_used_per_cip",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 21,
      headerName: t("HistoricCipTableStrings.acidPh"),
      field: "acid_cip_ph",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 22,
      headerName: t("HistoricCipTableStrings.acidTemperature", {
        unit: formatUnitForDisplay(temperature),
      }),
      field: "acid_cip_temp",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 23,
      headerName: t("HistoricCipTableStrings.acidDuration"),
      field: "acid_cip_duration",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 24,
      headerName: t("HistoricCipTableStrings.additiveAcidChemical"),
      field: "additive_acid_chemical_name",
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => getSectionFormattedValue(params),
      hide: !source,
    },
    {
      id: 25,
      headerName: t("HistoricCipTableStrings.additiveAcidPrice", {
        unit: pricePerMassUnit,
      }),
      field: "additive_acid_price",
      flex: 1,
      minWidth: 140,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 26,
      headerName: t("HistoricCipTableStrings.additiveAcidUsed", { unit: mass }),
      field: "additive_acid_used_per_cip",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 27,
      headerName: t("HistoricCipTableStrings.additiveAcidPh"),
      field: "additive_acid_ph",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 28,
      headerName: t("HistoricCipTableStrings.additiveAcidTemperature", {
        unit: formatUnitForDisplay(temperature),
      }),
      field: "additive_acid_temp",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 29,
      headerName: t("HistoricCipTableStrings.additiveAcidDuration"),
      field: "additive_acid_duration",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => getSectionFormattedValue(params, true),
      hide: !source,
    },
    {
      id: 30,
      headerName: t("HistoricCipTableStrings.is_tool_identified"),
      field: "Source",
      flex: 1,
      minWidth: 150,
      hide: Boolean(source),
      valueGetter: (params) => sourceFormatter(params, t),
    },
  ];

  if (enableActions) {
    columns.push({
      id: 31,
      width: 60,
      pinned: "right",
      cellRenderer: ({ data }) => (
        <ActionsPopover
          params={data}
          showLoader={showLoader}
          refreshTable={fetchCIPs}
        />
      ),
    });
  }

  return columns;
};
